export interface NutritionItem { name: string; calories: number; protein: number }

export interface NormalizedStat {
  id: string;
  source: string;
  contactId: string;
  date: string;
  meal: string;
  items: NutritionItem[];
  totalCalories: number;
  totalProtein: number;
}

export interface NutritionPoint { date: string; value: number }
export interface NutritionSeries { calories: NutritionPoint[]; protein: NutritionPoint[] }

export interface MealGroup { meal: string; calories: number; protein: number; items: NutritionItem[] }
export interface DayMeals { date: string; meals: MealGroup[]; totalCalories: number }

/**
 * Read a stat object regardless of whether its fields are nested under
 * `attributes` (portal endpoint) or flattened to the top level (generic
 * objects API used by the trainer module).
 */
function normalizeStat(stat: any): NormalizedStat {
  const a = stat?.attributes ?? stat ?? {};
  const items = Array.isArray(a.items) ? a.items : [];
  return {
    id: String(stat?.id ?? ''),
    source: String(a.source ?? ''),
    contactId: String(a.contactId ?? ''),
    date: String(a.date ?? ''),
    meal: String(a.meal ?? 'other'),
    items: items.map((it: any) => ({
      name: String(it?.name ?? 'Food'),
      calories: Number(it?.calories) || 0,
      protein: Number(it?.protein) || 0,
    })),
    totalCalories: Number(a.totalCalories) || 0,
    totalProtein: Number(a.totalProtein) || 0,
  };
}

/** Daily calorie + protein totals from fatsecret stats, chronological. */
export function dailyNutritionSeries(stats: any[]): NutritionSeries {
  const cal = new Map<string, number>();
  const pro = new Map<string, number>();
  for (const raw of stats) {
    const s = normalizeStat(raw);
    if (s.source !== 'fatsecret' || !s.date) continue;
    cal.set(s.date, (cal.get(s.date) ?? 0) + s.totalCalories);
    pro.set(s.date, (pro.get(s.date) ?? 0) + s.totalProtein);
  }
  const toSeries = (m: Map<string, number>): NutritionPoint[] =>
    [...m.entries()].sort((x, y) => (x[0] < y[0] ? -1 : 1)).map(([date, value]) => ({ date, value }));
  return { calories: toSeries(cal), protein: toSeries(pro) };
}

export interface NutritionSummary { today: number | null; avg7: number; avg30: number; proteinAvg30: number }

/** Average of the last `n` points (0 if none). */
function avgLastN(points: NutritionPoint[], n: number): number {
  const slice = points.slice(-n);
  if (slice.length === 0) return 0;
  return Math.round(slice.reduce((s, p) => s + p.value, 0) / slice.length);
}

/** Headline numbers for the nutrition Summary panel. */
export function nutritionSummary(series: NutritionSeries): NutritionSummary {
  const cal = series.calories;
  return {
    today: cal.length ? cal[cal.length - 1].value : null,
    avg7: avgLastN(cal, 7),
    avg30: avgLastN(cal, 30),
    proteinAvg30: avgLastN(series.protein, 30),
  };
}

/** Meals grouped by date → meal, most recent day first, capped to `days`. */
export function recentMeals(stats: any[], days = 5): DayMeals[] {
  const byDate = new Map<string, Map<string, MealGroup>>();
  for (const raw of stats) {
    const s = normalizeStat(raw);
    if (s.source !== 'fatsecret' || !s.date) continue;
    if (!byDate.has(s.date)) byDate.set(s.date, new Map());
    const meals = byDate.get(s.date)!;
    const g = meals.get(s.meal) ?? { meal: s.meal, calories: 0, protein: 0, items: [] };
    g.calories += s.totalCalories;
    g.protein += s.totalProtein;
    g.items.push(...s.items);
    meals.set(s.meal, g);
  }
  return [...byDate.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .slice(0, days)
    .map(([date, meals]) => {
      const list = [...meals.values()];
      return { date, meals: list, totalCalories: list.reduce((sum, m) => sum + m.calories, 0) };
    });
}
